import React, { useState } from "react";
import { NavLink, Outlet, Link } from "react-router-dom";
import {
  UtensilsCrossed,
  Tag,
  Settings,
  Image as ImageIcon,
  Scale,
  ShieldAlert,
  User,
  LogOut,
  Menu as MenuIcon,
  X,
  ArrowUpRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { ADMIN } from "@/lib/testIds";
import { useAdminAuth } from "@/lib/adminAuth";
import { AdminLogin } from "@/pages/admin/AdminLogin";

const navItems = [
  {
    to: "/admin",
    label: "Carte & Produits",
    icon: UtensilsCrossed,
    end: true,
    testId: ADMIN.sidebarLinkMenu,
  },
  {
    to: "/admin/offres",
    label: "Offres & Popup",
    icon: Tag,
    testId: "admin-sidebar-offres",
  },
  {
    to: "/admin/logo",
    label: "Logo / Branding",
    icon: ImageIcon,
    testId: "admin-sidebar-branding",
  },
  {
    to: "/admin/mentions-legales",
    label: "Mentions légales",
    icon: Scale,
    testId: "admin-sidebar-legal",
  },
  {
    to: "/admin/allergenes",
    label: "Allergènes",
    icon: ShieldAlert,
    testId: "admin-sidebar-allergens",
  },
  {
    to: "/admin/parametres",
    label: "Paramètres",
    icon: Settings,
    testId: ADMIN.sidebarLinkParametres,
  },
  {
    to: "/admin/compte",
    label: "Mon compte",
    icon: User,
    testId: "admin-sidebar-account",
  },
];

const SidebarContent = ({ onNavigate, onLogout, adminEmail }) => (
  <div className="flex h-full flex-col">
    <div className="px-6 pt-6 pb-6">
      <div className="font-display text-3xl text-white leading-none">
        LE <span className="text-[#FF9500]">11</span>
      </div>
      <div className="mt-1 text-[11px] uppercase tracking-[0.22em] text-white/40">
        Espace restaurateur
      </div>
      {adminEmail && (
        <div className="mt-3 text-[11px] text-white/55 truncate" title={adminEmail}>
          {adminEmail}
        </div>
      )}
    </div>

    <nav className="flex-1 px-3 space-y-1">
      {navItems.map(({ to, label, icon: Icon, end, testId }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          data-testid={testId}
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              "group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
              isActive
                ? "bg-[#FF9500] text-black font-semibold"
                : "text-white/70 hover:bg-white/5 hover:text-white"
            )
          }
        >
          <Icon size={18} className="shrink-0" />
          <span>{label}</span>
        </NavLink>
      ))}
    </nav>

    <div className="p-4 space-y-2">
      <button
        type="button"
        onClick={() => {
          onNavigate?.();
          onLogout?.();
        }}
        data-testid="admin-sidebar-logout"
        className="w-full flex items-center justify-between rounded-lg border border-[#DC2626]/30 bg-[#DC2626]/5 px-3 py-2.5 text-xs text-[#FCA5A5] hover:bg-[#DC2626]/15 transition-colors"
      >
        <span>Se déconnecter</span>
        <LogOut size={13} />
      </button>
      <Link
        to="/"
        onClick={onNavigate}
        className="flex items-center justify-between rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2.5 text-xs text-white/70 hover:text-white"
      >
        <span>Voir le site public</span>
        <ArrowUpRight size={14} />
      </Link>
    </div>
  </div>
);

export const AdminLayout = () => {
  const [open, setOpen] = useState(false);
  const { ready, admin, logout } = useAdminAuth();

  if (!ready) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center">
        <div className="font-street text-sm uppercase tracking-[0.28em] text-white/40">
          Vérification de la session…
        </div>
      </div>
    );
  }

  if (!admin) {
    return <AdminLogin />;
  }

  return (
    <div
      data-testid={ADMIN.root}
      className="min-h-screen bg-[#0A0A0A] text-white"
    >
      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-30 flex items-center justify-between border-b border-[#262626] bg-[#0A0A0A]/90 backdrop-blur px-4 h-14">
        <div className="font-display text-2xl">
          LE <span className="text-[#FF9500]">11</span>
          <span className="ml-2 text-[10px] uppercase tracking-[0.22em] text-[#A1A1AA]">
            Admin
          </span>
        </div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-[#262626]"
          aria-label="Ouvrir la navigation admin"
        >
          <MenuIcon size={18} />
        </button>
      </div>

      <div className="flex">
        {/* Desktop sidebar */}
        <aside
          data-testid={ADMIN.sidebar}
          className="hidden lg:flex w-64 shrink-0 flex-col bg-[#171717] min-h-screen sticky top-0"
        >
          <SidebarContent onLogout={logout} adminEmail={admin?.email} />
        </aside>

        {/* Mobile drawer */}
        {open && (
          <div className="lg:hidden fixed inset-0 z-40 flex">
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => setOpen(false)}
            />
            <aside className="relative w-72 bg-[#171717] shadow-xl">
              <button
                type="button"
                aria-label="Fermer"
                onClick={() => setOpen(false)}
                className="absolute right-3 top-3 inline-flex h-9 w-9 items-center justify-center rounded-lg text-white/80 hover:text-white"
              >
                <X size={18} />
              </button>
              <SidebarContent
                onNavigate={() => setOpen(false)}
                onLogout={logout}
                adminEmail={admin?.email}
              />
            </aside>
          </div>
        )}

        {/* Content */}
        <main className="flex-1 min-w-0">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 py-8 lg:py-12">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
